import { useState, useEffect } from "react";
import classes from "./AddAuditSummary.module.css";
import CommonFooter from "../../../CommonFooter/CommonFooter";
import InputField from "../../../ui/InputField/InputField";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useNavigate, useParams } from "react-router-dom";
import { env } from "../../../../constants/env";
import { useAuth } from "../../../../context/AuthContext";
import { toast } from "react-toastify";
import QuestionCard from "../../../ui/QuestionCard/QuestionCard";

const AddAuditSummary = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [loadingScores, setLoadingScores] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [currentSet, setCurrentSet] = useState(0);
  const [formData, setFormData] = useState({
    documentsRecords: "",
    organizationLeadership: "",
    personnelManagement: "",
    customerFocus: "",
    equipmentManagement: "",
    assessment: "",
    supplierInventoryManagement: "",
    processManagement: "",
    informationManagement: "",
    nonconformingEvents: "",
    continualImprovement: "",
    facilitiesSafety: "",
  });

  const [availableScores, setAvailableScores] = useState({});

  const sectionNames = {
    documentsRecords: "Documents & Records",
    organizationLeadership: "Organization & Leadership",
    personnelManagement: "Personnel Management",
    customerFocus: "Customer Focus",
    equipmentManagement: "Equipment Management",
    assessment: "Assessment",
    supplierInventoryManagement: "Inventory Management",
    processManagement: "Process Management",
    informationManagement: "Information Management",
    nonconformingEvents: "Non-Conforming Events",
    continualImprovement: "Continual Improvement",
    facilitiesSafety: "Facilities & Safety",
  };

  useEffect(() => {
    const fetchAvailableScores = async () => {
      try {
        setLoadingScores(true);
        const scores = {};

        await Promise.all(
          Object.entries(sectionNames).map(async ([key, section]) => {
            const res = await fetch(
              `${
                env.kataApiUrl
              }/api/certification/not-attached-scores?labId=${labId}&section=${encodeURIComponent(
                section
              )}&name=SLIPTA`
            );
            const data = await res.json();
            scores[key] = data;
          })
        );

        setAvailableScores(scores);
      } catch (err) {
        console.error("Failed to fetch available scores", err);
        toast.error("Failed to load available scores");
      } finally {
        setLoadingScores(false);
      }
    };

    if (labId) {
      fetchAvailableScores();
    }
  }, [labId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const handleDone = async () => {
    try {
      setSubmitting(true);

      const sections = Object.entries(sectionNames).reduce(
        (acc, [key, label]) => {
          const value = formData[key];
          acc[label] = value || 0;
          return acc;
        },
        {}
      );

      const response = await fetch(
        `${env.kataApiUrl}/api/certification/audit-summary/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ labId, sections }),
        }
      );

      const result = await response.json();

      if (!response.ok)
        throw new Error(result.message || "Failed to create audit summary");

      toast.success("Audit summary created successfully");
      navigate(-1);
    } catch (err) {
      console.error("Error submitting summary", err);
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const questionSets = [
    {
      content: (
        <div className="d-flex flex-column gap-4">
          <div className="row">
            <QuestionCard question="Fill this" />
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 1: Documents and records" />
            </div>
            <div className="col-5">
              <InputField
                name="documentsRecords"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.documentsRecords || []}
                value={formData.documentsRecords}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 22" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 2: Organization and leadership" />
            </div>
            <div className="col-5">
              <InputField
                name="organizationLeadership"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.organizationLeadership || []}
                value={formData.organizationLeadership}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 26" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 3: Personnel management" />
            </div>
            <div className="col-5">
              <InputField
                name="personnelManagement"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.personnelManagement || []}
                value={formData.personnelManagement}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 34" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 4: Customer Focus" />
            </div>
            <div className="col-5">
              <InputField
                name="customerFocus"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.customerFocus || []}
                value={formData.customerFocus}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 24" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 5: Equipment Management" />
            </div>
            <div className="col-5">
              <InputField
                name="equipmentManagement"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.equipmentManagement || []}
                value={formData.equipmentManagement}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 44" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 6: Assessment" />
            </div>
            <div className="col-5">
              <InputField
                name="assessment"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.assessment || []}
                value={formData.assessment}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 24" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 7: Supplier and Inventory Management" />
            </div>
            <div className="col-5">
              <InputField
                name="supplierInventoryManagement"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={
                  availableScores.supplierInventoryManagement || []
                }
                value={formData.supplierInventoryManagement}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 27" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 8: Process Management" />
            </div>
            <div className="col-5">
              <InputField
                name="processManagement"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.processManagement || []}
                value={formData.processManagement}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 71" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 9: Information Management" />
            </div>
            <div className="col-5">
              <InputField
                name="informationManagement"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.informationManagement || []}
                value={formData.informationManagement}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 24" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 10: Nonconforming Events" />
            </div>
            <div className="col-5">
              <InputField
                name="nonconformingEvents"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.nonconformingEvents || []}
                value={formData.nonconformingEvents}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 13" />
            </div>
          </div>

          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 11: Continual Improvement" />
            </div>
            <div className="col-5">
              <InputField
                name="continualImprovement"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.continualImprovement || []}
                value={formData.continualImprovement}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 07" />
            </div>
          </div>
          <div className="row">
            <div className="col-4 ps-0">
              <QuestionCard question="Section 12: Facilities and Safety" />
            </div>
            <div className="col-5">
              <InputField
                name="facilitiesSafety"
                placeholder="Select an option"
                dropdownClass={classes.customInput}
                dropdownOptions={availableScores.facilitiesSafety || []}
                value={formData.facilitiesSafety}
                onChange={handleChange}
              />
            </div>
            <div className="col-3 pe-0">
              <QuestionCard question="Max score: 57" />
            </div>
          </div>
        </div>
      ),
    },
  ];

  const { content } = questionSets[currentSet];

  return (
    <div className={classes.wrapper}>
      <div
        className="d-flex align-items-center gap-2 mb-4"
        onClick={() => navigate(-1)}
        role="button"
      >
        <IoIosArrowRoundBack size={35} />
        <h2 className="m-0">Add new</h2>
      </div>

      {/* Loading screen */}
      {loadingScores ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className={classes.questionContainer}>{content}</div>
      )}

      <CommonFooter
        handlePrevious={handlePrevious}
        handleCancel={() => navigate(-1)}
        handleDone={handleDone}
        questionSets={questionSets}
        currentSet={currentSet}
        cancelText="Cancel"
        doneText={submitting ? "Creating..." : "Create"}
        disabled={submitting}
      />
    </div>
  );
};

export default AddAuditSummary;
